import React, { useEffect, useState } from "react";
import axios from "axios";
import { base_url } from "../../context/Authcontext";
import ToastMessage from "../ToastMessage";

// USDT on Ethereum (ERC-20). Rate, wallet and minimums come from the backend
// (backend/src/config/usdt.js) so the page can never show a different rate
// from the one the server applies.

const ETH_ADDRESS = /^0x[0-9a-fA-F]{40}$/;
const ETH_TX_HASH = /^0x[0-9a-fA-F]{64}$/;
const USDT_AMOUNT = /^\d+(\.\d{1,2})?$/;

const request = { withCredentials: true, timeout: 30000, headers: { Accept: "application/json" } };

const inr = (value) =>
  Number(value).toLocaleString("en-IN", { maximumFractionDigits: 2 });

function useUsdtConfig() {
  const [config, setConfig] = useState(null);
  useEffect(() => {
    axios
      .get(`${base_url}/api/webapi/usdt/config`, request)
      .then((res) => res.data?.status && setConfig(res.data.data))
      .catch(() => ToastMessage(0, "Could not load USDT details"));
  }, []);
  return config;
}

const panelText = { color: "var(--dark-theme-white-color)", fontFamily: "var(--rb-font)" };

const warningStyle = {
  background: "rgba(255, 193, 7, 0.15)",
  border: "1px solid #ffc107",
  borderRadius: 6,
  padding: "8px 10px",
  fontSize: 13,
  margin: "10px 0",
};

function NetworkWarning({ config }) {
  return (
    <div style={warningStyle}>
      Only USDT on <b>{config.networkLabel}</b>. USDT sent on any other network
      (TRC-20, BEP-20 and so on) cannot be recovered.
    </div>
  );
}

export function UsdtDeposit({ onSubmitted }) {
  const config = useUsdtConfig();
  const [form, setForm] = useState({ usdtAmount: "", txHash: "", proof: null });
  const [submitting, setSubmitting] = useState(false);

  if (!config) return <div className="p-3" style={panelText}>Loading USDT details…</div>;

  const amountValid = USDT_AMOUNT.test(form.usdtAmount);
  const credit = amountValid ? Math.floor(Number(form.usdtAmount) * config.inrRate) : 0;

  const copyAddress = () => {
    navigator.clipboard.writeText(config.walletAddress);
    ToastMessage(1, "Address copied");
  };

  const onFile = (e) => {
    const file = e.target.files[0];
    if (file && (!file.type?.startsWith("image/") || file.size > 5 * 1024 * 1024)) {
      ToastMessage(0, "Upload an image of 5MB or less");
      e.target.value = "";
      return setForm((f) => ({ ...f, proof: null }));
    }
    setForm((f) => ({ ...f, proof: file || null }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const formEl = e.target;
    if (submitting) return;
    if (!amountValid || Number(form.usdtAmount) < config.minDepositUsdt) {
      return ToastMessage(0, `Minimum deposit is ${config.minDepositUsdt} USDT`);
    }
    const txHash = form.txHash.trim();
    if (!ETH_TX_HASH.test(txHash)) {
      return ToastMessage(0, "Enter the transaction hash: 0x followed by 64 characters");
    }
    if (!form.proof) return ToastMessage(0, "Payment proof image is required");

    const body = new FormData();
    body.append("usdtAmount", form.usdtAmount);
    body.append("txHash", txHash);
    body.append("paymentProof", form.proof);

    setSubmitting(true);
    try {
      const res = await axios.post(`${base_url}/api/webapi/deposite/usdt`, body, {
        ...request,
        headers: { ...request.headers, "Content-Type": "multipart/form-data" },
      });
      if (res.data.status) {
        ToastMessage(1, res.data.message);
        setForm({ usdtAmount: "", txHash: "", proof: null });
        formEl.reset?.();
        onSubmitted?.();
      } else {
        ToastMessage(0, res.data.message);
      }
    } catch (err) {
      ToastMessage(0, err?.code === "ECONNABORTED" ? "Request timed out — please try again" : "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <app-manual-deposit className="ng-star-inserted">
      <div className="tab-content ng-star-inserted" style={{ background: "#7dc4ff" }}>
        <form onSubmit={submit} noValidate className="p-2" style={panelText}>
          <div className="heading">PAY WITH USDT ({config.network})</div>

          <div className="text-center my-2">
            <img
              src={config.qrImage}
              alt={`USDT ${config.network} deposit address QR code`}
              style={{ width: 180, height: 180, background: "#fff", borderRadius: 8, padding: 6 }}
            />
          </div>

          <div className="withdraw_text">
            <p className="bb_head mb-1">Wallet owner</p>
            <b>{config.walletOwner}</b>
          </div>
          <div className="withdraw_text mt-2">
            <p className="bb_head mb-1">Network</p>
            <b>{config.networkLabel}</b>
          </div>
          <div className="withdraw_text mt-2">
            <p className="bb_head mb-1">Wallet address</p>
            <div className="d-flex align-items-center gap-2">
              <code style={{ wordBreak: "break-all", color: "inherit" }}>{config.walletAddress}</code>
              <img
                src="/assets/images/copy.svg"
                alt="Copy address"
                className="click_btn"
                style={{ cursor: "pointer", width: 20 }}
                onClick={copyAddress}
              />
            </div>
          </div>
          <div className="withdraw_text mt-2">
            <p className="bb_head mb-1">Rate</p>
            <b>1 USDT = ₹{config.inrRate}</b>
          </div>

          <NetworkWarning config={config} />

          <div className="withdraw_text">
            <p className="bb_head">Amount sent (USDT)</p>
            <div className="input_dec select_dec2 marg-dec">
              <input
                type="text"
                inputMode="decimal"
                name="usdtAmount"
                autoComplete="off"
                placeholder={`Minimum ${config.minDepositUsdt} USDT`}
                className="amount text-white w-100 p-2 rounded"
                value={form.usdtAmount}
                onChange={(e) => setForm((f) => ({ ...f, usdtAmount: e.target.value.trim() }))}
              />
            </div>
            {credit > 0 && <small>You will be credited ₹{inr(credit)}</small>}
          </div>

          <div className="withdraw_text mt-2">
            <p className="bb_head">Transaction hash</p>
            <div className="input_dec select_dec2 marg-dec">
              <input
                type="text"
                name="txHash"
                autoComplete="off"
                placeholder="0x…"
                maxLength={66}
                className="amount text-white w-100 p-2 rounded"
                value={form.txHash}
                onChange={(e) => setForm((f) => ({ ...f, txHash: e.target.value }))}
              />
            </div>
          </div>

          <div className="withdraw_text mt-2">
            <p className="bb_head">Payment proof image</p>
            <div className="input_dec select_dec2 marg-dec">
              <input type="file" name="paymentProof" accept="image/*" onChange={onFile} />
            </div>
          </div>

          <button
            className="bank-submit-btn cursor-pointer mt-3"
            type="submit"
            disabled={submitting}
            aria-busy={submitting}
          >
            {submitting ? "Submitting..." : "submit"}
          </button>
        </form>
      </div>
    </app-manual-deposit>
  );
}

export function UsdtWithdraw({ onSubmitted }) {
  const config = useUsdtConfig();
  const [usdtAmount, setUsdtAmount] = useState("");
  const [address, setAddress] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Prefill the address the player last withdrew to.
  useEffect(() => {
    axios
      .get(`${base_url}/api/webapi/withdraw/usdt_address`, request)
      .then((res) => {
        const saved = res.data?.account;
        if (saved?.isAvailable && ETH_ADDRESS.test(saved.usdtAddress || "")) {
          setAddress(saved.usdtAddress);
        }
      })
      .catch(() => {});
  }, []);

  if (!config) return null;

  const amountValid = USDT_AMOUNT.test(usdtAmount);
  const debit = amountValid ? Math.round(Number(usdtAmount) * config.inrRate * 100) / 100 : 0;

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!amountValid || Number(usdtAmount) < config.minWithdrawUsdt) {
      return ToastMessage(0, `Minimum withdrawal is ${config.minWithdrawUsdt} USDT`);
    }
    if (!ETH_ADDRESS.test(address.trim())) {
      return ToastMessage(0, "Enter a valid Ethereum (ERC-20) wallet address");
    }

    setSubmitting(true);
    try {
      const res = await axios.post(
        `${base_url}/api/webapi/withdraw/create`,
        { withdrawalMethod: "USDT_ADDRESS", withdrawAmount: usdtAmount, usdtAddress: address.trim() },
        request
      );
      if (res.data.status) {
        ToastMessage(1, res.data.message);
        setUsdtAmount("");
        onSubmitted?.();
      } else {
        ToastMessage(0, res.data.message);
      }
    } catch (err) {
      ToastMessage(0, err?.code === "ECONNABORTED" ? "Request timed out — please try again" : "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} noValidate className="p-3 my-2 rounded" style={{ ...panelText, border: "1px solid rgba(255,255,255,0.2)" }}>
      <h5 className="mb-2" style={panelText}>Withdraw in USDT ({config.network})</h5>
      <div className="mb-2">1 USDT = ₹{config.inrRate}</div>

      <label className="d-block mb-1">Amount (USDT)</label>
      <input
        type="text"
        inputMode="decimal"
        autoComplete="off"
        placeholder={`Minimum ${config.minWithdrawUsdt} USDT`}
        className="form-control mb-1"
        value={usdtAmount}
        onChange={(e) => setUsdtAmount(e.target.value.trim())}
      />
      {debit > 0 && <small className="d-block mb-2">₹{inr(debit)} will be deducted from your balance</small>}

      <label className="d-block mb-1 mt-2">Your {config.networkLabel} wallet address</label>
      <input
        type="text"
        autoComplete="off"
        placeholder="0x…"
        maxLength={42}
        className="form-control"
        value={address}
        onChange={(e) => setAddress(e.target.value)}
      />

      <NetworkWarning config={config} />

      <button className="btn btn-primary w-100" type="submit" disabled={submitting} aria-busy={submitting}>
        {submitting ? "Submitting..." : "Withdraw USDT"}
      </button>
    </form>
  );
}
